import { PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID } from '@billing/validation/tariff';
import {
  ISO_DATE_FORMAT,
  isDateOnlyString,
  parseUnknownDateTime,
} from 'utils/dateParsing';

const DATE_RANGE_FILTER_IDS = new Set([
  PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID,
  'createdAt',
  'serviceStart',
  'claim.serviceStart',
  'submissions.submittedAt',
  'remittances.parsedAt',
  'claims.serviceStart',
]);

/**
 * Converts MRT/filter input values into backend date query tokens (`YYYY-MM-DD`).
 * Use when serializing date filter values into URLs or API query strings.
 */
export const formatDateFilterToken = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (isDateOnlyString(value)) return value.trim();

  const parsed = parseUnknownDateTime(value);
  return parsed.isValid ? parsed.toFormat(ISO_DATE_FORMAT) : null;
};

const normalizeDateFilterPart = (value: unknown) => {
  const parsed = parseUnknownDateTime(value);
  return parsed.isValid ? parsed.toJSDate() : value;
};

/**
 * Normalizes known date-range filters into Date objects for MRT date-range
 * columns. This keeps the JS Date boundary centralized in one utility.
 */
export const normalizeDateRangeFilter = <
  T extends { id: string; value: unknown }
>(
  filter: T
): T => {
  if (!DATE_RANGE_FILTER_IDS.has(filter.id) || !Array.isArray(filter.value)) {
    return filter;
  }

  const [start, end] = filter.value;
  return {
    ...filter,
    value: [normalizeDateFilterPart(start), normalizeDateFilterPart(end)],
  };
};

/**
 * Identifies column filter IDs that should be treated as date ranges by table
 * filter/query helpers.
 */
export const isDateRangeFilterID = (id: string) =>
  DATE_RANGE_FILTER_IDS.has(id);
